// Conciliación diaria — Portal Admin VentasLuga (F3 · pista Retail 🔵).
// Mockup: `surekeep/mockups/backoffice/portal-admin-vl-conciliacion.html`.
//
// ⚠️ EL BACKEND ES DE OTRO EQUIPO. Este portal solo CONSUME: los folios y los depósitos los
// produce el settlement. La UI se construye contra un mock tipado y el entregable es
// `settlementContract.ts` — la forma a acordar, no una decisión tomada.
//
// La pregunta de la pantalla es una sola: ¿lo que la tienda cobró coincide con lo que depositó?
// Por eso el grid se lee por color antes que por número, y la celda con diferencia es la única que
// grita.
import { useState } from 'react';
import { ErrorState, Skeleton } from '@/kit';
import { cn } from '@/lib/cn';
import { formatDateMx, formatDateTimeMx } from '@/lib/dateHelpers';
import type { ReconciliationStatus } from '@/api/services/retail-wizard/backoffice';
import { useFolios, useReconciliation } from './api/useConciliacion';
import { cellKey, cellsByKey, dayLabel, statusLabel } from './lib/conciliacionGrid';

const MXN = new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' });
const money = (cents: number) => MXN.format(cents / 100);
const moneyShort = (cents: number) => {
    const pesos = cents / 100;
    if (Math.abs(pesos) >= 1000) return `$${(pesos / 1000).toFixed(1)}K`;
    return `$${String(Math.round(pesos))}`;
};

/** El color dice el estado antes que el número: la celda con diferencia es la única que grita. */
const STATUS_CELL: Record<ReconciliationStatus, string> = {
    MATCHED: 'bg-success-soft/60 text-success',
    MISMATCH: 'bg-destructive/15 text-destructive font-bold',
    PENDING_DEPOSIT: 'bg-warning-soft/70 text-warning',
    NO_ACTIVITY: 'text-muted-foreground/50',
};

function asError(error: unknown): Error | null {
    if (!error) return null;
    if (error instanceof Error) return error;
    return new Error(typeof error === 'string' ? error : 'Error desconocido');
}

function Kpi({
    label,
    value,
    sub,
    valueClass,
}: {
    label: string;
    value: React.ReactNode;
    sub: string;
    valueClass?: string;
}) {
    return (
        <div className="flex flex-none flex-col justify-center gap-0.5 border-r border-border px-3.5 last:border-r-0">
            <span className="text-[9.5px] font-semibold uppercase tracking-[0.05em] text-muted-foreground">
                {label}
            </span>
            <span className={cn('text-[15px] font-semibold tabular-nums', valueClass)}>
                {value}
            </span>
            <span className="text-[9.5px] text-muted-foreground">{sub}</span>
        </div>
    );
}

export function ConciliacionPage() {
    const [selected, setSelected] = useState<{ date: string; storeId: string } | null>(null);
    const query = useReconciliation();
    const folios = useFolios(selected);

    const data = query.data;
    const error = asError(query.error);
    // El Map se construye SIEMPRE desde `cellsByKey`, aunque no haya datos: un `new Map()` suelto
    // se infiere `Map<any, any>` y degrada a `any` todo lo que se lea de las celdas, justo donde
    // el tipado importa.
    const byKey = cellsByKey(data?.cells ?? []);
    const selectedStore = data?.stores.find((s) => s.storeId === selected?.storeId);

    return (
        <div className="flex h-full min-h-0 flex-col">
            <div className="mt-2 flex flex-none items-stretch overflow-x-auto rounded-t-[10px] border border-border bg-card py-2">
                <Kpi
                    label="Cobrado hoy"
                    value={data ? money(data.kpis.collectedTodayCents) : '—'}
                    sub={data ? `${String(data.kpis.foliosToday)} folios generados` : '— folios'}
                />
                <Kpi
                    label="Cobrado semana"
                    value={data ? money(data.kpis.collectedWeekCents) : '—'}
                    sub={data ? `${String(data.kpis.foliosWeek)} folios acumulados` : '— folios'}
                />
                <Kpi
                    label="Última remesa"
                    value={
                        data?.kpis.lastRemittance
                            ? money(data.kpis.lastRemittance.amountCents)
                            : '—'
                    }
                    sub={
                        data?.kpis.lastRemittance
                            ? formatDateMx(data.kpis.lastRemittance.at)
                            : 'sin remesas'
                    }
                />
                <Kpi
                    label="Próximo depósito"
                    value={data ? money(data.kpis.nextRemittanceCents) : '—'}
                    sub="acumulado de la semana en curso"
                />
                <Kpi
                    label="Tiendas en alerta"
                    value={data?.kpis.storesOnAlert ?? '—'}
                    sub="tope anti-fraude > 85% consumido"
                    valueClass="text-warning"
                />
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto rounded-b-[10px] border-x border-b border-border bg-background p-2">
                {error ? (
                    <ErrorState
                        title="No se pudo cargar la conciliación"
                        description={error.message}
                        onRetry={() => {
                            void query.refetch();
                        }}
                    />
                ) : query.isPending || !data ? (
                    <Skeleton lines={10} />
                ) : (
                    <div className="flex flex-col gap-2">
                        <section className="rounded-lg border border-border bg-card">
                            <div className="flex items-baseline justify-between border-b border-border px-3 py-2">
                                <span className="text-[12px] font-semibold">
                                    1 · Cobros por día × tienda · últimos 7 días
                                </span>
                                <span className="text-[10px] text-muted-foreground">
                                    Clic en una celda para ver sus folios
                                </span>
                            </div>
                            <div className="overflow-x-auto p-2">
                                <table className="w-full border-collapse">
                                    <thead>
                                        <tr>
                                            <th
                                                scope="col"
                                                className="px-2 py-1 text-left text-[9.5px] font-bold uppercase tracking-[0.04em] text-muted-foreground"
                                            >
                                                Tienda
                                            </th>
                                            {data.dates.map((date) => (
                                                <th
                                                    key={date}
                                                    scope="col"
                                                    className="px-1 py-1 text-center text-[9.5px] font-bold uppercase tracking-[0.04em] text-muted-foreground"
                                                >
                                                    {dayLabel(date)}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.stores.map((store) => (
                                            <tr key={store.storeId}>
                                                <td
                                                    title={store.storeName}
                                                    className="max-w-[200px] truncate border-b border-border/50 px-2 py-1 text-[11px]"
                                                >
                                                    {store.storeName}
                                                </td>
                                                {data.dates.map((date) => {
                                                    const cell = byKey.get(
                                                        cellKey(date, store.storeId),
                                                    );
                                                    const isSelected =
                                                        selected?.date === date &&
                                                        selected.storeId === store.storeId;
                                                    return (
                                                        <td
                                                            key={date}
                                                            className="border-b border-border/50 p-0.5"
                                                        >
                                                            <button
                                                                type="button"
                                                                disabled={
                                                                    !cell ||
                                                                    cell.status === 'NO_ACTIVITY'
                                                                }
                                                                onClick={() => {
                                                                    setSelected({
                                                                        date,
                                                                        storeId: store.storeId,
                                                                    });
                                                                }}
                                                                title={
                                                                    cell
                                                                        ? `${statusLabel(cell.status)} · ${String(cell.folioCount)} folios`
                                                                        : undefined
                                                                }
                                                                className={cn(
                                                                    'w-full rounded px-1 py-1 text-center text-[10.5px] tabular-nums transition-colors',
                                                                    cell
                                                                        ? STATUS_CELL[cell.status]
                                                                        : '',
                                                                    isSelected &&
                                                                        'ring-2 ring-primary ring-offset-1',
                                                                    cell &&
                                                                        cell.status !==
                                                                            'NO_ACTIVITY' &&
                                                                        'cursor-pointer hover:brightness-95',
                                                                )}
                                                            >
                                                                {!cell ||
                                                                cell.status === 'NO_ACTIVITY'
                                                                    ? '·'
                                                                    : moneyShort(
                                                                          cell.collectedCents,
                                                                      )}
                                                            </button>
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                <div className="mt-2 flex flex-wrap gap-3 text-[10px] text-muted-foreground">
                                    <span className="flex items-center gap-1">
                                        <span className="size-2.5 rounded-sm bg-success-soft" />{' '}
                                        Cuadra
                                    </span>
                                    <span className="flex items-center gap-1">
                                        <span className="size-2.5 rounded-sm bg-destructive/30" />{' '}
                                        Con diferencia
                                    </span>
                                    <span className="flex items-center gap-1">
                                        <span className="size-2.5 rounded-sm bg-warning-soft" />{' '}
                                        Falta depositar
                                    </span>
                                </div>
                            </div>
                        </section>

                        <section className="rounded-lg border border-border bg-card">
                            <div className="flex items-baseline justify-between border-b border-border px-3 py-2">
                                <span className="text-[12px] font-semibold">
                                    2 · Detalle ·{' '}
                                    {selected && selectedStore
                                        ? `${selectedStore.storeName} · ${formatDateMx(`${selected.date}T12:00:00Z`)}`
                                        : 'selecciona una celda'}
                                </span>
                                {selected ? (
                                    <span className="text-[10px] text-muted-foreground">
                                        {(() => {
                                            const cell = byKey.get(
                                                cellKey(selected.date, selected.storeId),
                                            );
                                            if (!cell) return '';
                                            return `Cobrado ${money(cell.collectedCents)} · depositado ${money(cell.depositedCents)} · diferencia ${money(cell.differenceCents)}`;
                                        })()}
                                    </span>
                                ) : null}
                            </div>
                            <div className="overflow-x-auto p-2">
                                {!selected ? (
                                    <p className="py-6 text-center text-[11px] text-muted-foreground">
                                        Elige una celda del grid para ver sus folios.
                                    </p>
                                ) : folios.isPending ? (
                                    <Skeleton lines={5} />
                                ) : (folios.data?.items.length ?? 0) === 0 ? (
                                    <p className="py-6 text-center text-[11px] text-muted-foreground">
                                        Sin folios ese día en esta tienda.
                                    </p>
                                ) : (
                                    <table className="w-full min-w-[680px] border-collapse">
                                        <thead>
                                            <tr className="text-left text-[9.5px] font-bold uppercase tracking-[0.04em] text-muted-foreground">
                                                <th scope="col" className="px-2 py-1">
                                                    Hora
                                                </th>
                                                <th scope="col" className="px-2 py-1">
                                                    Folio
                                                </th>
                                                <th scope="col" className="px-2 py-1">
                                                    Concepto
                                                </th>
                                                <th scope="col" className="px-2 py-1">
                                                    Vendedor
                                                </th>
                                                <th scope="col" className="px-2 py-1 text-right">
                                                    Monto
                                                </th>
                                                <th scope="col" className="px-2 py-1">
                                                    Estado
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {folios.data?.items.map((folio) => (
                                                <tr
                                                    key={folio.folioId}
                                                    className="whitespace-nowrap border-b border-border/50 text-[11px]"
                                                >
                                                    <td className="px-2 py-1 tabular-nums">
                                                        {formatDateTimeMx(folio.at).split(' ')[1]}
                                                    </td>
                                                    <td className="px-2 py-1 font-mono text-[10.5px]">
                                                        {/* eslint-disable-next-line no-restricted-syntax -- folio de negocio, el número con el que se cruza contra el depósito */}
                                                        {folio.folioId}
                                                    </td>
                                                    <td
                                                        title={folio.concept}
                                                        className="max-w-[240px] truncate px-2 py-1"
                                                    >
                                                        {folio.concept}
                                                    </td>
                                                    <td className="px-2 py-1">
                                                        {folio.sellerName}
                                                    </td>
                                                    <td
                                                        className={cn(
                                                            'px-2 py-1 text-right font-semibold tabular-nums',
                                                            folio.amountCents < 0 &&
                                                                'text-destructive',
                                                        )}
                                                    >
                                                        {money(folio.amountCents)}
                                                    </td>
                                                    <td className="px-2 py-1">
                                                        <span
                                                            className={cn(
                                                                'rounded-full border px-1.5 py-px text-[9px] font-semibold',
                                                                folio.settled
                                                                    ? 'border-success text-success'
                                                                    : 'border-warning text-warning',
                                                            )}
                                                        >
                                                            {folio.settled
                                                                ? 'Depositado'
                                                                : 'Pendiente'}
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )}
                            </div>
                        </section>

                        <section className="rounded-lg border border-border bg-card">
                            <div className="flex items-baseline justify-between border-b border-border px-3 py-2">
                                <span className="text-[12px] font-semibold">
                                    3 · Tope anti-fraude por tienda
                                </span>
                                <span className="text-[10px] text-muted-foreground">
                                    Solo lectura · lo configura el equipo de settlement
                                </span>
                            </div>
                            <div className="p-2">
                                {data.caps.map((cap) => (
                                    <div
                                        key={cap.storeId}
                                        className="grid grid-cols-[160px_1fr_120px_50px] items-center gap-2 py-1 text-[11px]"
                                    >
                                        <span className="truncate">{cap.storeName}</span>
                                        <div className="h-2 overflow-hidden rounded-full bg-muted">
                                            <div
                                                className={cn(
                                                    'h-full rounded-full',
                                                    cap.usage > 0.85 ? 'bg-warning' : 'bg-success',
                                                )}
                                                style={{
                                                    width: `${String(Math.min(Math.round(cap.usage * 100), 100))}%`,
                                                }}
                                            />
                                        </div>
                                        <span className="text-right tabular-nums text-muted-foreground">
                                            {money(cap.currentCents)} / {money(cap.capCents)}
                                        </span>
                                        <span
                                            className={cn(
                                                'text-right font-bold tabular-nums',
                                                cap.usage > 0.85 && 'text-warning',
                                            )}
                                        >
                                            {Math.round(cap.usage * 100)}%
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </section>
                    </div>
                )}
            </div>
        </div>
    );
}
